import { type Application, RendererType } from "pixi.js";
import { WarLook } from "../war/warLook";
import { t } from "./translations/phrases";

interface Notice {
    title: string
    body: string
    /** the error, in small print (for screenshots) */
    detail?: string
}

/**
 * Keeps the game drawing when a frame goes wrong.
 *
 * Pixi's ticker asks the browser for the next frame only after the current one has run: if anything it runs throws
 * (the game's own update, or Pixi drawing the scene), no next frame is ever asked for, and the game freezes or goes
 * blank for good while the page around it keeps working. Here a failed frame is logged (once per kind of error) and
 * the next one runs as usual, so a fault lasts only as long as whatever causes it.
 *
 * If drawing keeps failing (a second and a half of frames in a row), the likeliest culprit is the Battlefield look's
 * shaders on this graphics card or browser: the look drops to Low, which has none. If drawing still fails then, a
 * notice shows the error, so a player can tell what they see instead of reporting a blank screen.
 *
 * A browser without WebGL (graphics acceleration turned off, or a graphics card the browser won't use) gets Pixi's
 * Canvas 2D renderer, which draws with the processor: the game runs there on the Low look (see WarLook), and the
 * menu's own warning (#splash-hw-acceleration-warning, ui.ts) says why it may be slow.
 */
class FrameGuardClass {
    private readonly _seen = new Set<string>();
    /** frames in a row that failed to draw */
    private _failures = 0;
    /** the notices shown so far, by kind: each shows at most once per visit */
    private readonly _shown = new Set<string>();

    /** wrap the ticker's frame so that one that throws doesn't stop the next; watch for the graphics card giving up */
    protect(app: Application): void {
        const ticker = app.ticker;
        const update = ticker.update.bind(ticker);
        ticker.update = (currentTime?: number): void => {
            try {
                update(currentTime);
                this._failures = 0;
            } catch (error) {
                // nothing may escape from here: that is exactly what stops the ticker
                try {
                    this.failed("draw", error);
                } catch {
                    // the frame is lost either way
                }
            }
        };

        // Two failures that throw nothing and leave the world blank, both likeliest with the look's full-screen
        // shaders on a weak or unusual graphics card: a shader the card can't compile (Pixi only logs it), and the
        // card resetting under the load (the browser then takes the canvas's context away). Either way: Low, after
        // this frame (never in the middle of Pixi drawing).
        const consoleError = console.error.bind(console);
        console.error = (...args: unknown[]): void => {
            consoleError(...args);
            if (typeof args[0] === "string" && args[0].includes("Could not initialize shader")) {
                setTimeout(() => this._graphicsTrouble("A shader couldn't be compiled on this graphics card"), 0);
            }
        };
        // a lost context usually comes back by itself (Pixi rebuilds everything when it does): only one that stays lost
        // at Low is worth a notice
        let restored = 0;
        app.canvas.addEventListener("webglcontextrestored", () => { restored++; });
        app.canvas.addEventListener("webglcontextlost", () => {
            const before = restored;
            setTimeout(() => {
                if (this._lowerLook("The graphics card stopped drawing the game (its context was lost)")) return;
                setTimeout(() => {
                    if (restored === before) this._cantDraw("The graphics card stopped drawing the game (its WebGL context was lost)");
                }, 3000);
            }, 0);
        });

        // for anyone reading the console of a player with a slow or odd-looking game
        if (app.renderer.type === RendererType.CANVAS) {
            console.warn("This browser offers no WebGL: the game is drawn without the graphics card (Canvas 2D), on the Low look");
        }
    }

    private _graphicsTrouble(message: string): void {
        if (!this._lowerLook(message)) this._cantDraw(message);
    }

    private _cantDraw(message: string): void {
        this._show("cant-draw", {
            title: t("The game can't draw on this computer right now."),
            body: t("Try Settings → Graphics: Shaders Low, Shadows Off, Lighting & atmosphere Off, or Render mode WebGL 1, and reload. If it keeps happening, send a screenshot of this message."),
            // (the error itself, for whoever reads the screenshot: as the console says it)
            detail: `Error: ${message.slice(0, 300)}`
        });
    }

    /** true when the look had shaders to drop (now Low) */
    private _lowerLook(reason: string): boolean {
        try {
            if (!WarLook.dropShadersAfterError()) return false;
        } catch (error) {
            // the setting is Low already (it's set first): if drawing still fails there, the notice comes next time
            console.error("Lowering the Battlefield look failed:", error);
        }
        console.warn(`${reason}: the Battlefield look went to Low`);
        return true;
    }

    /** a frame's update (the game's own code) or its drawing (Pixi) threw */
    failed(stage: "update" | "draw", error: unknown): void {
        const message = error instanceof Error ? error.message : String(error);
        const key = `${stage}: ${message}`;
        if (!this._seen.has(key)) {
            this._seen.add(key);
            console.error(`A frame failed to ${stage === "draw" ? "draw" : "update"} (the next one carries on):`, error);
        }
        if (stage !== "draw") return;
        if (++this._failures < 90) return;
        // a second and a half of frames that wouldn't draw: lighter look first, the notice if it's already light
        // (after this frame: lowering the look rebuilds the map's textures, which must not happen inside a failing draw)
        this._failures = 0;
        setTimeout(() => this._graphicsTrouble(message), 0);
    }

    private _show(kind: string, { title: titleText, body: bodyText, detail: detailText }: Notice): void {
        if (this._shown.has(kind)) return;
        this._shown.add(kind);
        const notice = document.createElement("div");
        notice.setAttribute("role", "alert");
        notice.dataset.notice = kind;
        notice.style.cssText = [
            "position:fixed", "left:50%", "top:16px", "transform:translateX(-50%)", "z-index:100000",
            "max-width:min(560px,calc(100vw - 32px))", "box-sizing:border-box", "padding:12px 40px 12px 16px",
            "background:rgba(20,22,16,.94)", "border:1px solid #8a7a4f", "border-radius:8px", "color:#ece4d1",
            "font:14px/1.4 Inter,Arial,sans-serif", "box-shadow:0 6px 24px rgba(0,0,0,.5)"
        ].join(";");
        const title = document.createElement("strong");
        title.textContent = titleText;
        const body = document.createElement("div");
        body.style.marginTop = "4px";
        body.textContent = bodyText;
        notice.append(title, body);
        if (detailText) {
            const detail = document.createElement("div");
            detail.style.cssText = "margin-top:6px;font:12px/1.35 ui-monospace,Consolas,monospace;color:#c9bfa5;word-break:break-word";
            detail.textContent = detailText;
            notice.append(detail);
        }
        const close = document.createElement("button");
        close.type = "button";
        close.textContent = "✕";
        close.setAttribute("aria-label", t("Close"));
        close.style.cssText = "position:absolute;top:6px;right:8px;background:none;border:0;color:#ece4d1;font-size:16px;cursor:pointer;padding:4px";
        close.addEventListener("click", () => notice.remove());
        notice.append(close);
        document.body.appendChild(notice);
    }
}

export const FrameGuard = new FrameGuardClass();
